import React, { useEffect, useState, useCallback } from 'react';
import { experiences } from '../mock';
import { Briefcase, MapPin, Layers, ChevronRight, Building2, ArrowUp } from 'lucide-react';
import BackToTop from './BackToTop';

const ExperienceTopLink = () => {
  const handleClick = (event) => {
    event.preventDefault();
    const el = document.getElementById('experience');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <div className="mt-6 flex justify-end">
      <a
        href="#experience"
        onClick={handleClick}
        className="group inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#5b6b7d] hover:text-[#c9a961] transition-colors"
      >
        <ArrowUp size={12} className="text-[#c9a961]" />
        <span>Top of Experience</span>
      </a>
    </div>
  );
};

const SubRole = ({ title, points }) => (
  <div className="bg-[#fbf9f4] border border-[#0c1a2b]/8 p-6">
    <div className="flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-[#c9a961]" />
      <h4 className="font-display text-lg text-[#0c1a2b]">{title}</h4>
    </div>
    <ul className="mt-4 space-y-2.5 text-[14px] text-[#3a4d62] leading-relaxed">
      {points.map((p) => (
        <li key={p} className="flex items-start gap-2">
          <span className="text-[#c9a961] mt-1.5">—</span>
          <span>{p}</span>
        </li>
      ))}
    </ul>
  </div>
);

const ModuleCard = ({ name, tagline, features }) => (
  <div className="bg-[#07111f] text-white p-6 border border-white/10 hover:border-[#c9a961]/50 transition-colors">
    <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#c9a961]">
      <Layers size={12} />
      <span>Module</span>
    </div>
    <h4 className="font-display text-xl mt-2">{name}</h4>
    <p className="text-[13px] text-white/65 mt-2 leading-relaxed">{tagline}</p>
    <div className="h-px bg-white/10 my-4" />
    <ul className="space-y-2 text-[13px] text-white/80">
      {features.map((f) => (
        <li key={f} className="flex items-start gap-2">
          <ChevronRight size={13} className="text-[#c9a961] mt-1 shrink-0" />
          <span>{f}</span>
        </li>
      ))}
    </ul>
  </div>
);

const FeaturedExperience = ({ e }) => (
  <div id={`exp-${e.slug}`} className="relative scroll-mt-32">
    <div className="absolute left-3 md:left-1/2 top-3 -translate-x-1/2 timeline-dot" />
    <div className="pl-10 md:pl-0 md:px-12">
      <div className="card-elevated p-7 md:p-10">
        <div className="flex flex-wrap items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-[#c9a961] mb-3">
          <span className="inline-flex items-center gap-2"><Briefcase size={13} /> {e.period}</span>
          <span className="opacity-50">•</span>
          <span className="inline-flex items-center gap-2"><Building2 size={12} /> Featured Engagement</span>
        </div>
        <h3 className="font-display text-2xl md:text-3xl text-[#0c1a2b]">{e.role}</h3>
        <div className="mt-1 inline-flex items-center gap-2 text-sm text-[#5b6b7d]">
          <span className="font-medium text-[#1c3a5e]">{e.company}</span>
          <span className="opacity-50">•</span>
          <span className="inline-flex items-center gap-1"><MapPin size={12} /> {e.location}</span>
        </div>
        <p className="mt-4 text-[15px] leading-relaxed text-[#1c2f44] max-w-3xl">{e.summary}</p>

        {e.roles && e.roles.length > 0 && (
          <div className="mt-8">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#5b6b7d] mb-4">Roles held</div>
            <div className="grid lg:grid-cols-3 gap-5">
              {e.roles.map((r) => (
                <SubRole key={r.title} title={r.title} points={r.points} />
              ))}
            </div>
          </div>
        )}

        {e.project && (
          <div className="mt-10 pt-8 border-t border-[#0c1a2b]/10">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#c9a961]">{e.project.title}</div>
            <p className="mt-3 text-[15px] leading-relaxed text-[#1c2f44] max-w-4xl">
              {e.project.overview}
            </p>
            <div className="mt-7 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {e.project.modules.map((m) => (
                <ModuleCard key={m.name} {...m} />
              ))}
            </div>
          </div>
        )}
        <ExperienceTopLink />
      </div>
    </div>
  </div>
);

const StandardExperience = ({ e, idx }) => {
  const left = idx % 2 === 0;
  return (
    <div id={`exp-${e.slug}`} className="relative grid md:grid-cols-2 gap-6 md:gap-12 scroll-mt-32">
      <div className="absolute left-3 md:left-1/2 top-3 -translate-x-1/2 timeline-dot" />
      <div className={`pl-10 md:pl-0 ${left ? 'md:pr-12 md:text-right' : 'md:col-start-2 md:pl-12'}`}>
        <div className="card-elevated p-7">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#c9a961] mb-3">
            <Briefcase size={13} />
            <span>{e.period}</span>
          </div>
          <h3 className="font-display text-2xl text-[#0c1a2b]">{e.role}</h3>
          <div className={`mt-1 inline-flex items-center gap-2 text-sm text-[#5b6b7d] ${left ? 'md:flex-row-reverse' : ''}`}>
            <span className="font-medium text-[#1c3a5e]">{e.company}</span>
            <span className="opacity-50">•</span>
            <span className="inline-flex items-center gap-1"><MapPin size={12} /> {e.location}</span>
          </div>
          <p className="mt-4 text-[15px] leading-relaxed text-[#1c2f44]">{e.summary}</p>
          {e.bullets && (
            <ul className={`mt-4 space-y-2 text-[14px] text-[#3a4d62] ${left ? 'md:text-right' : ''}`}>
              {e.bullets.map((b) => (
                <li key={b} className="leading-relaxed">— {b}</li>
              ))}
            </ul>
          )}
          <ExperienceTopLink />
        </div>
      </div>
      <div className="hidden md:block" />
    </div>
  );
};

const ExperienceTabs = ({ items, activeSlug, onSelect }) => (
  <div className="mt-10 -mx-2 px-2 overflow-x-auto">
    <div className="inline-flex items-center gap-2 min-w-full md:min-w-0 border border-[#0c1a2b]/10 bg-white/70 backdrop-blur p-1.5 rounded-sm">
      {items.map((it) => {
        const isActive = activeSlug === it.slug;
        return (
          <button
            key={it.slug}
            type="button"
            onClick={() => onSelect(it.slug)}
            className={`whitespace-nowrap text-xs md:text-[13px] tracking-wide px-3.5 md:px-4 py-2 rounded-sm transition-colors duration-200 ${
              isActive
                ? 'bg-[#0c1a2b] text-[#c9a961]'
                : 'text-[#3a4d62] hover:bg-[#0c1a2b]/[0.06] hover:text-[#0c1a2b]'
            }`}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  </div>
);

const useActiveExperience = (slugs) => {
  const [active, setActive] = useState(slugs[0] || '');

  useEffect(() => {
    if (typeof window === 'undefined' || !slugs.length) return undefined;

    const computeActive = () => {
      const triggerY = window.scrollY + 220;
      let current = slugs[0];
      slugs.forEach((slug) => {
        const el = document.getElementById(`exp-${slug}`);
        if (el && el.offsetTop <= triggerY) {
          current = slug;
        }
      });
      setActive((prev) => (prev === current ? prev : current));
    };

    computeActive();
    window.addEventListener('scroll', computeActive, { passive: true });
    return () => window.removeEventListener('scroll', computeActive);
  }, [slugs]);

  return active;
};

const Experience = () => {
  const tabItems = experiences.map((e) => ({
    slug: e.slug,
    label: e.tabLabel || e.company,
  }));
  const slugs = tabItems.map((t) => t.slug);
  const activeSlug = useActiveExperience(slugs);

  const onSelect = useCallback((slug) => {
    const el = document.getElementById(`exp-${slug}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <section id="experience" className="bg-paper py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="section-eyebrow">Career Journey</div>
          <h2 className="font-display text-4xl md:text-5xl text-[#0c1a2b] mt-4 leading-tight">
            12+ years across global financial institutions.
          </h2>
          <div className="gold-line mt-6" />
        </div>

        <ExperienceTabs items={tabItems} activeSlug={activeSlug} onSelect={onSelect} />

        <div className="mt-12 relative">
          <div className="absolute left-3 md:left-1/2 top-2 bottom-2 w-px bg-[#0c1a2b]/15 md:-translate-x-1/2" />
          <div className="space-y-14">
            {experiences.map((e, idx) =>
              e.featured ? (
                <FeaturedExperience key={e.slug || e.company + e.period} e={e} />
              ) : (
                <StandardExperience key={e.slug || e.company + e.period} e={e} idx={idx} />
              )
            )}
          </div>
        </div>

        <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-6">
          <BackToTop targetId="experience" label="Top of Experience" variant="light" />
          <span className="hidden sm:inline-block w-px h-5 bg-[#0c1a2b]/15" />
          <BackToTop targetId="top" label="Top of Page" variant="light" />
        </div>
      </div>
    </section>
  );
};

export default Experience;
